<script lang="ts">
	/**
	 * How many seats, for a per-seat price (§9.7).
	 *
	 * The bounds come from the price and are enforced here **and** on the server — this stops the
	 * customer submitting a count the plan forbids, and the server stops everything else. A control
	 * that clamped silently would be worse than one that refuses: the customer would ask for
	 * fifteen seats, be given ten, and only find out on the invoice.
	 *
	 * The total is shown beside the count because a per-seat unit price is the one figure customers
	 * reliably misread — "£5" next to a Buy button reads as the price of the plan, not of one seat.
	 */
	import { Button, Input } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import type { StorefrontCurrency } from '../AppTypes';
	import PriceDisplay from './PriceDisplay.svelte';

	export let seats: number;
	/**
	 * What one of these is called (§4.1). Seats on a subscription, units on a one-time product sold
	 * by the unit — the same control and the same arithmetic, and the customer is not buying users.
	 *
	 * A pair rather than one word with an `s` appended: the label is read aloud by the aria-labels
	 * below, and an English plural rule guessed in a component is the sort of thing that survives
	 * until the first noun that does not take one.
	 */
	export let noun: string = 'user';
	export let nounPlural: string = 'users';
	/**
	 * The field's accessible name — what the control is asking for, capitalised as a label. Kept
	 * separate from the nouns above because "how many users" and "how many items" are labelled
	 * "Users" and "Quantity", and only the caller knows which question it is putting.
	 */
	export let label: string = 'Users';
	export let seatMin: number = 1;
	export let seatMax: number | null = null;
	/** The **unit** amount in minor units, or null when the price is unavailable in this currency. */
	export let unitAmount: number | null = null;
	export let currency: StorefrontCurrency | undefined = undefined;
	export let disabled: boolean = false;
	export let id: string = 'seat-selector';

	/**
	 * Emitted whenever the count actually moves.
	 *
	 * A consumer holding a price quote needs to know, because the quote is only true for the count
	 * it was asked about — without this it would keep showing a figure the server never gave for
	 * the number now on screen.
	 */
	const dispatcher = createEventDispatcher();

	function fnSet(next: number): void {
		if (next === seats) return;
		seats = next;
		dispatcher('change', { seats: next });
	}

	$: canDecrease = !disabled && seats > seatMin;
	$: canIncrease = !disabled && (seatMax === null || seats < seatMax);
	$: total = unitAmount === null ? null : unitAmount * seats;

	/**
	 * The typed value, clamped. Declared here rather than inline because a TypeScript cast in the
	 * markup does not parse.
	 */
	function fnCommitEvent(event: Event): void {
		fnCommit((event.target as HTMLInputElement).value);
	}

	/** Whatever the customer typed, clamped to the bounds and to a whole number. */
	function fnCommit(raw: string): void {
		const parsed = Number(raw);
		if (!Number.isFinite(parsed)) {
			fnSet(seatMin);
			return;
		}
		const whole = Math.floor(parsed);
		const ceiling = seatMax ?? whole;
		fnSet(Math.min(Math.max(whole, seatMin), Math.max(ceiling, seatMin)));
	}

	$: boundsLabel =
		seatMax === null
			? `${seatMin} ${seatMin === 1 ? noun : nounPlural} minimum`
			: `${seatMin}–${seatMax} ${nounPlural}`;
</script>

<div class="space-y-2">
	<div class="flex items-center gap-2">
		<Button
			color="alternative"
			size="sm"
			class="!px-3"
			disabled={!canDecrease}
			aria-label={`Remove one ${noun}`}
			on:click={() => fnSet(Math.max(seatMin, seats - 1))}
		>
			<i class="fa-solid fa-minus" />
		</Button>

		<Input
			{id}
			type="number"
			class="w-24 text-center"
			min={seatMin}
			max={seatMax ?? undefined}
			value={seats}
			{disabled}
			aria-label={label}
			on:change={fnCommitEvent}
		/>

		<Button
			color="alternative"
			size="sm"
			class="!px-3"
			disabled={!canIncrease}
			aria-label={`Add one ${noun}`}
			on:click={() => fnSet(seatMax === null ? seats + 1 : Math.min(seatMax, seats + 1))}
		>
			<i class="fa-solid fa-plus" />
		</Button>

		<span class="text-sm text-default">{boundsLabel}</span>
	</div>

	{#if total !== null && currency !== undefined}
		<p class="text-sm text-default">
			<!--
				Both figures, always. The unit price alone is the number customers misread as the
				cost of the plan; the total alone hides how it was arrived at.
			-->
			<PriceDisplay amount={unitAmount} {currency} /> per {noun} ·
			<strong class="text-gray-900 dark:text-white">
				<PriceDisplay amount={total} {currency} />
			</strong>
			total
		</p>
	{/if}
</div>
